import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  formatEuroDisplayOnBlur,
  sanitizeEuroDisplay,
  scrollToFirstError,
  toEuroDot,
  validateEuroControl,
} from '../../payments-amount.utils';
import {
  CODICE_FISCALE_PATTERN,
  PaymentSummary,
  formatEuro,
  parseEuroInput,
  quoteServiceFee,
  requireText,
} from '../../payments.models';
import { PaymentSummaryComponent } from '../payment-summary/payment-summary.component';

@Component({
  selector: 'app-donazione-form',
  imports: [ReactiveFormsModule, PaymentSummaryComponent],
  templateUrl: './donazione-form.component.html',
  styleUrl: './donazione-form.component.scss',
})
export class DonazioneFormComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);

  readonly summary = signal<PaymentSummary | null>(null);
  readonly showFormError = signal(false);

  readonly form = this.formBuilder.group({
    importo: ['', Validators.required],
    nome: ['', Validators.required],
    cognome: ['', Validators.required],
    codiceFiscale: ['', [Validators.required, Validators.pattern(CODICE_FISCALE_PATTERN)]],
    note: [''],
  });

  /** Valore importo con punto, per logica e backend. */
  get importoDot(): string {
    return toEuroDot(this.form.controls.importo.value ?? '');
  }

  goBack(): void {
    void this.router.navigate(['/pagamenti']);
  }

  onImportoInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = sanitizeEuroDisplay(input.value);
    this.form.controls.importo.setValue(sanitized, { emitEvent: false });
    input.value = sanitized;
    if (this.form.controls.importo.touched) {
      validateEuroControl(this.form.controls.importo);
    }
  }

  onImportoBlur(): void {
    const formatted = formatEuroDisplayOnBlur(this.form.controls.importo.value ?? '');
    if (formatted) {
      this.form.controls.importo.setValue(formatted);
    }
    this.form.controls.importo.markAsTouched();
    validateEuroControl(this.form.controls.importo);
  }

  onCodiceFiscaleBlur(): void {
    const value = this.form.controls.codiceFiscale.value?.trim().toUpperCase() ?? '';
    this.form.controls.codiceFiscale.setValue(value);
  }

  submit(): void {
    this.onImportoBlur();
    this.onCodiceFiscaleBlur();

    const { nome, cognome, codiceFiscale, note } = this.form.getRawValue();
    requireText(this.form.controls.nome, nome);
    requireText(this.form.controls.cognome, cognome);
    requireText(this.form.controls.codiceFiscale, codiceFiscale);
    validateEuroControl(this.form.controls.importo);

    const codice = codiceFiscale?.trim().toUpperCase() ?? '';
    if (codice && !CODICE_FISCALE_PATTERN.test(codice)) {
      this.form.controls.codiceFiscale.setErrors({ pattern: true });
    }

    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.showFormError.set(true);
      scrollToFirstError(this.form);
      return;
    }

    this.showFormError.set(false);
    const cents = parseEuroInput(this.importoDot)!;
    this.summary.set({
      kind: 'donazione',
      title: 'Donazione Libera',
      tag: 'Solidarietà',
      nome: nome!.trim(),
      cognome: cognome!.trim(),
      codiceFiscale: codice,
      amountLabel: 'Importo',
      amountValue: formatEuro(cents),
      note: note?.trim() || undefined,
      baseCents: cents,
      ...quoteServiceFee(cents),
    });
  }

  closeSummary(): void {
    this.summary.set(null);
  }

  pay(): void {
    if (!this.summary()) {
      return;
    }
  }
}
